import { Directive, ElementRef, HostBinding, HostListener } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ImageViewerModalComponent } from '../components/modals/image-viewer-modal/image-viewer-modal.component';

const PLACEHOLDER_IMAGE = 'default-image.png';

/**
 * Al tocar la imagen de un evento o producto la abre ampliada en un modal, sin navegar
 * ni disparar el click del contenedor (una fila, una card). La imagen por defecto no se amplía:
 * no hay nada que ver, y el toque sigue su camino normal.
 */
@Directive({
  selector: 'img[appImageZoom]',
  standalone: false,
})
export class ImageZoomDirective {
  private opening = false;

  constructor(
    private readonly element: ElementRef<HTMLImageElement>,
    private readonly modalCtrl: ModalController,
  ) {}

  @HostBinding('attr.role') get role(): string | null {
    return this.zoomable ? 'button' : null;
  }

  @HostBinding('attr.tabindex') get tabindex(): number | null {
    return this.zoomable ? 0 : null;
  }

  @HostBinding('style.cursor') get cursor(): string | null {
    return this.zoomable ? 'zoom-in' : null;
  }

  @HostListener('click', ['$event'])
  @HostListener('keydown.enter', ['$event'])
  @HostListener('keydown.space', ['$event'])
  async open(event: Event): Promise<void> {
    if (!this.zoomable || this.opening) return;

    event.stopPropagation();
    event.preventDefault();
    this.opening = true;
    try {
      const image = this.element.nativeElement;
      await ImageViewerModalComponent.open(this.modalCtrl, { src: image.currentSrc || image.src, alt: image.alt });
    } finally {
      this.opening = false;
    }
  }

  private get zoomable(): boolean {
    const src = this.element.nativeElement.currentSrc || this.element.nativeElement.getAttribute('src') || '';
    return src !== '' && !src.endsWith(PLACEHOLDER_IMAGE);
  }
}
